import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Lock, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';

export const PasswordRecovery: React.FC = () => {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Optional: Redirect to login/dashboard after success
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                window.location.href = '/';
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const handleReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // The user is already logged in via the magic link hash
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) throw error;
            setSuccess(true);
        } catch (err: any) {
            setError(err.message || 'Failed to update password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
                
                <div className="text-center mb-8">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Lock className="text-blue-600" size={24} />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Set New Password</h2>
                    <p className="text-slate-500 mt-2">Please enter your new password below.</p>
                </div>

                {success ? (
                    <div className="text-center space-y-4 animate-in fade-in zoom-in duration-300">
                        <div className="bg-green-50 text-green-700 p-4 rounded-xl flex flex-col items-center gap-2 border border-green-100">
                            <CheckCircle2 size={32} />
                            <span className="font-medium">Password updated successfully!</span>
                        </div>
                        <p className="text-sm text-slate-500">Redirecting you to dashboard...</p>
                    </div>
                ) : (
                    <form onSubmit={handleReset} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                New Password
                            </label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                placeholder="••••••••"
                            />
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-100">
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Updating...' : 'Update Password'}
                            {!loading && <ArrowRight size={18} />}
                        </button>
                    </form>
                )}
            </div>
            
            <div className="mt-8 text-slate-400 text-sm">
                &copy; 2026 Powerpack V4
            </div>
        </div>
    );
};
